import { useEffect, useRef, type ReactElement } from "react";
import { Button, Input, Popover, TextField, Tooltip } from "@heroui/react";
import { ChevronDown, Lock, LockOpen, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { drawingStore } from "./drawingStore";
import { DRAWING_COLORS, DRAWING_WIDTHS, type Drawing, type DrawingLineStyle } from "./types";

const LINE_STYLES: { id: DrawingLineStyle; label: string; dash: string }[] = [
  { id: "solid", label: "Solid", dash: "" },
  { id: "dashed", label: "Dashed", dash: "5 4" },
  { id: "dotted", label: "Dotted", dash: "1.5 3" },
];

/** Drawings whose line can change width / style. */
const HAS_LINE = new Set<Drawing["type"]>([
  "trendline", "horizontal", "vertical", "channel", "rectangle", "ellipse", "triangle", "arrow",
]);

interface DrawingStyleBarProps {
  symbol: string;
  /** The selected drawings (one or more). */
  selected: Drawing[];
  /** Bumped when a text note should take the keyboard (just placed / double-clicked). */
  editRequest: { id: string; n: number } | null;
  className?: string;
}

function LineSample({ width, dash }: { width: number; dash: string }) {
  return (
    <svg width="22" height="8" aria-hidden className="shrink-0">
      <line x1="1" y1="4" x2="21" y2="4" stroke="currentColor" strokeWidth={width} strokeDasharray={dash} strokeLinecap="round" />
    </svg>
  );
}

function IconTip({ label, children }: { label: string; children: ReactElement }) {
  return (
    <Tooltip delay={300}>
      {children}
      <Tooltip.Content>{label}</Tooltip.Content>
    </Tooltip>
  );
}

/**
 * The selected drawing's pill (Binance's floating drawing toolbar): colour,
 * line, the note's text, lock, delete. Applies to every selected drawing it fits.
 */
export function DrawingStyleBar({ symbol, selected, editRequest, className }: DrawingStyleBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  // One undo step per typing session: the first keystroke records history.
  const freshEdit = useRef(true);
  const text = selected.length === 1 && selected[0]!.type === "text" ? selected[0]! : null;

  useEffect(() => {
    if (editRequest && text && editRequest.id === text.id) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editRequest, text?.id]);

  if (selected.length === 0) return null;

  const colorable = selected.filter(d => d.type !== "position");
  const lined = selected.filter(d => HAS_LINE.has(d.type));
  const color = colorable[0]?.color;
  const width = lined[0]?.width ?? 2;
  const style = lined[0]?.lineStyle ?? "solid";
  const locked = selected.every(d => d.locked);
  const patchAll = (list: Drawing[], patch: Partial<Drawing>) =>
    drawingStore.change(symbol, { updated: list.map(d => ({ ...d, ...patch })) });
  const divider = <span className="mx-0.5 h-5 w-px bg-separator" aria-hidden />;

  return (
    <div
      role="toolbar"
      aria-label={selected.length === 1 ? "Selected drawing" : `${selected.length} selected drawings`}
      className={cn("flex h-10 items-center gap-0.5 rounded-xl border border-border bg-surface px-1 shadow-lg", className)}
    >
      {colorable.length > 0 && (
        <Popover>
          <Button size="sm" variant="ghost" aria-label="Colour" className="gap-1 px-1.5">
            <span className="size-4 rounded-full border border-border" style={{ background: color }} />
            <ChevronDown className="size-3 text-muted" />
          </Button>
          <Popover.Content placement="top">
            <Popover.Dialog aria-label="Colour" className="grid grid-cols-4 gap-2 p-3">
              {DRAWING_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Colour ${c}`}
                  aria-pressed={c === color}
                  onClick={() => patchAll(colorable, { color: c })}
                  className={cn(
                    "size-7 rounded-lg border border-border outline-none focus-visible:ring-2 focus-visible:ring-focus",
                    c === color && "ring-2 ring-foreground ring-offset-2 ring-offset-surface",
                  )}
                  style={{ background: c }}
                />
              ))}
            </Popover.Dialog>
          </Popover.Content>
        </Popover>
      )}

      {lined.length > 0 && (
        <Popover>
          <Button size="sm" variant="ghost" aria-label={`Line: ${width}px ${style}`} className="gap-1 px-1.5 text-foreground">
            <LineSample width={Math.min(width, 3)} dash={LINE_STYLES.find(s => s.id === style)!.dash} />
            <span className="num text-2xs">{width}px</span>
            <ChevronDown className="size-3 text-muted" />
          </Button>
          <Popover.Content placement="top">
            <Popover.Dialog aria-label="Line" className="w-52 space-y-3 p-3">
              <div className="space-y-1.5">
                <p className="text-2xs text-muted">Thickness</p>
                <div className="grid grid-cols-4 gap-1" role="group" aria-label="Thickness">
                  {DRAWING_WIDTHS.map(w => (
                    <button
                      key={w}
                      type="button"
                      aria-pressed={w === width}
                      onClick={() => patchAll(lined, { width: w })}
                      className={cn(
                        "flex h-8 flex-col items-center justify-center gap-0.5 rounded-md text-muted outline-none hover:bg-default hover:text-foreground focus-visible:ring-2 focus-visible:ring-focus",
                        w === width && "bg-default text-foreground",
                      )}
                    >
                      <span className="w-4 rounded-full bg-current" style={{ height: w }} />
                      <span className="num text-3xs">{w}px</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <p className="text-2xs text-muted">Style</p>
                <div className="grid grid-cols-3 gap-1" role="group" aria-label="Line style">
                  {LINE_STYLES.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      aria-pressed={s.id === style}
                      onClick={() => patchAll(lined, { lineStyle: s.id })}
                      className={cn(
                        "flex h-8 flex-col items-center justify-center gap-0.5 rounded-md text-muted outline-none hover:bg-default hover:text-foreground focus-visible:ring-2 focus-visible:ring-focus",
                        s.id === style && "bg-default text-foreground",
                      )}
                    >
                      <LineSample width={2} dash={s.dash} />
                      <span className="text-3xs">{s.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </Popover.Dialog>
          </Popover.Content>
        </Popover>
      )}

      {text && (
        <>
          {divider}
          <TextField
            aria-label="Note text"
            value={text.text ?? ""}
            onFocus={() => (freshEdit.current = true)}
            onChange={v => {
              drawingStore.update(symbol, text.id, { text: v }, freshEdit.current);
              freshEdit.current = false;
            }}
            onKeyDown={e => {
              if (e.key === "Enter") inputRef.current?.blur();
            }}
          >
            <Input ref={inputRef} placeholder="Note" className="h-7 w-40 text-sm" />
          </TextField>
        </>
      )}

      {divider}
      <IconTip label={locked ? "Unlock: allow moving" : "Lock: stop accidental moves"}>
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          aria-label={locked ? "Unlock: allow moving" : "Lock: stop accidental moves"}
          aria-pressed={locked}
          onPress={() => patchAll(selected, { locked: !locked })}
          className={cn("text-muted hover:text-foreground", locked && "text-foreground")}
        >
          {locked ? <Lock className="size-4" /> : <LockOpen className="size-4" />}
        </Button>
      </IconTip>
      <IconTip label="Delete (Del)">
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          aria-label="Delete (Del)"
          onPress={() => drawingStore.change(symbol, { removed: selected.map(d => d.id) })}
          className="text-muted hover:text-danger"
        >
          <Trash2 className="size-4" />
        </Button>
      </IconTip>
    </div>
  );
}
